import { FC } from 'react';
import Modal from 'react-modal';
import { PrimaryButton, SecondaryButton } from '..';
import { Input } from '../shared/custom-inputs';
import { useDeliveryRates } from '../../../hooks';
import {
  DeliveryRateResponse,
  DeliveryRateType,
} from '../../../types/delivery-rate.types';

export const CreateRateModal: FC<{
  isOpen: boolean;
  onClose: () => void;
  deliveryRate?: DeliveryRateResponse | null;
}> = ({ isOpen, onClose, deliveryRate }) => {
  const isEditMode = !!deliveryRate;

  const { formData, isSubmitting, handleInputChange, handleSubmit } =
    useDeliveryRates(onClose, deliveryRate);

  // Helper function to convert boolean to select value
  const booleanToSelect = (value: boolean) => (value ? 'Yes' : 'No');

  // Handle select input change for boolean values
  const handleBooleanSelectChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const { name } = e.target;
    const value = e.target.value === 'Yes';

    // Create a custom event object with the modified value
    const customEvent = {
      target: {
        name,
        value,
        type: 'select',
      },
    };

    // Call the original handleInputChange with our custom value
    handleInputChange(customEvent as any);
  };

  return (
    <Modal
      isOpen={isOpen}
      ariaHideApp={false}
      onRequestClose={onClose}
      overlayClassName="modal-overlay"
      className={`w-11/12 sm:w-6/12 h-10/12 default-modal`}
    >
      <div className="p-6 bg-white overflow-y-auto h-full">
        <div className="mb-6">
          <h2 className="text-xl font-bold opacity-80">
            {isEditMode ? 'Edit Delivery Rate' : 'Setup Delivery Rate'}
          </h2>
          <p className="text-gray-500">
            Configure delivery rates for the platform
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div className="flex flex-col gap-6">
              <div className="bg-gray-50 p-4 rounded-md">
                <h3 className="text-base font-bold mb-1">Rate Type</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs semibold leading-7 text-neutral-900">
                      Rate Type
                    </label>
                    <select
                      name="rateType"
                      value={formData.rateType}
                      onChange={handleInputChange}
                      className="w-full p-3 border border-gray-300 rounded-[4px] focus:outline-none focus:ring-4 focus:ring-gray-300 focus:ring-opacity-30 focus:border-gray-500"
                    >
                      {Object.values(DeliveryRateType).map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs semibold leading-7 text-neutral-900">
                      Active
                    </label>
                    <select
                      name="isActive"
                      value={booleanToSelect(formData.isActive)}
                      onChange={handleBooleanSelectChange}
                      className="w-full p-3 border border-gray-300 rounded-[4px] focus:outline-none focus:ring-4 focus:ring-gray-300 focus:ring-opacity-30 focus:border-gray-500"
                    >
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </div>
                </div>
                <div className="mt-4">
                  <div>
                    <label className="text-xs semibold leading-7 text-neutral-900">
                      Rate Name
                    </label>
                    <input
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Enter a descriptive name for this rate"
                      className="w-full p-3 border border-gray-300 rounded-[4px] focus:outline-none focus:ring-4 focus:ring-gray-300 focus:ring-opacity-30 focus:border-gray-500"
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-md">
                <h3 className="text-base font-bold mb-1">
                  Standard Configuration
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  Establish the standard delivery rate.
                </p>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <Input
                      label="Min Distance"
                      name="minDistance"
                      type="number"
                      value={formData.minDistance}
                      onChange={handleInputChange}
                      prependText="Metre"
                      required
                    />
                  </div>
                  <div>
                    <Input
                      label="Max Distance"
                      name="maxDistance"
                      type="number"
                      value={formData.maxDistance}
                      onChange={handleInputChange}
                      prependText="Metre"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <Input
                      label="Min Price"
                      name="minPrice"
                      type="number"
                      value={formData.minPrice}
                      onChange={handleInputChange}
                      prependText="₦"
                      required
                    />
                  </div>
                  <div>
                    <Input
                      label="Max Price"
                      name="maxPrice"
                      type="number"
                      value={formData.maxPrice}
                      onChange={handleInputChange}
                      prependText="₦"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Input
                      label="Rate"
                      name="rate"
                      type="number"
                      value={formData.rate}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
              </div>

              {formData.rateType === DeliveryRateType.WEATHER && (
                <div className="bg-gray-50 p-4 rounded-md">
                  <h3 className="text-base font-bold mb-1">
                    Weather Configuration
                  </h3>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <Input
                        label="Weather Multiplier"
                        name="weatherMultiplier"
                        type="number"
                        step="0.1"
                        value={formData.weatherMultiplier}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div>
                      <label className="text-xs semibold leading-7 text-neutral-900">
                        Apply Weather Rate
                      </label>
                      <select
                        name="isWeatherAffectingRate"
                        value={booleanToSelect(formData.isWeatherAffectingRate)}
                        onChange={handleBooleanSelectChange}
                        className="w-full p-3 border border-gray-300 rounded-[4px] focus:outline-none focus:ring-4 focus:ring-gray-300 focus:ring-opacity-30 focus:border-gray-500"
                      >
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {formData.rateType === DeliveryRateType.SURGE && (
                <div className="bg-gray-50 p-4 rounded-md">
                  <h3 className="text-base font-bold mb-1">
                    Surge Configuration
                  </h3>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <Input
                        label="Surge Multiplier"
                        name="surgeMultiplier"
                        type="number"
                        step="0.1"
                        value={formData.surgeMultiplier}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div>
                      <Input
                        label="Surge Threshold"
                        name="surgeThreshold"
                        type="number"
                        step="0.1"
                        value={formData.surgeThreshold}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs semibold leading-7 text-neutral-900">
                      Apply Surge Rate
                    </label>
                    <select
                      name="isSurgeAffectingRate"
                      value={booleanToSelect(formData.isSurgeAffectingRate)}
                      onChange={handleBooleanSelectChange}
                      className="w-full p-3 border border-gray-300 rounded-[4px] focus:outline-none focus:ring-4 focus:ring-gray-300 focus:ring-opacity-30 focus:border-gray-500"
                    >
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end mt-6 space-x-4">
            <SecondaryButton text="Cancel" onClick={onClose} />
            <PrimaryButton
              text={isEditMode ? 'Update Rate' : 'Save Changes'}
              type="submit"
              disabled={isSubmitting}
            />
          </div>
        </form>
      </div>
    </Modal>
  );
};
